/* global React, ReactDOM */
// Browser globals (window, document, fetch, crypto, sessionStorage, history)
// are provided by ESLint's globals.browser preset under the flat config.
/*
 * CheckoutModal — HB-PAY Task P4.1
 *
 * Accessibility:
 *   role="dialog" + aria-modal="true" + aria-labelledby.
 *   Escape closes, Tab is trapped within the dialog, focus returns to trigger on close.
 *
 * Protocol:
 *   POST /api/pay/create-order with Idempotency-Key header.
 *   200 -> build <form> from formFields, submit() to WayForPay hosted checkout.
 *   202 -> show "Check your email" state with countdown resend button.
 *   4xx/5xx -> map `type` (application/problem+json) to translated error.
 *
 * Bot protection:
 *   A Cloudflare Turnstile widget is rendered inside the modal on every open.
 *   The submit handler awaits the challenge token before POSTing; the server
 *   rejects any request without a real token (see services/payments/src/lib/
 *   turnstile.ts). In dev builds where window.__TURNSTILE_SITE_KEY__ is unset
 *   and the server's TURNSTILE_SECRET starts with DEV_BYPASS_, the fallback
 *   string `"dev-bypass-token"` is used. This fallback is REJECTED in prod.
 *
 * URL scrubbing:
 *   After a successful submit we strip the `?mlt=<token>` param from the
 *   address bar via history.replaceState so the single-use magic-link token
 *   does not leak into browser history, Referer headers, or client-side
 *   telemetry.
 *
 * Fallback strings (A10 defensive):
 *   `FALLBACK_STRINGS` mirrors the pricing.checkout.* keys in UK for the
 *   narrow window before window.i18n finishes loading. Removed once HB-RU
 *   is permanently merged to main.
 */

const {
  useCallback: useCallbackCM,
  useEffect: useEffectCM,
  useRef: useRefCM,
  useState: useStateCM,
} = React;

// ---------------- Fallback strings ----------------
const FALLBACK_STRINGS = {
  "pricing.checkout.modal.title": "Оформлення підписки",
  "pricing.checkout.modal.close": "Закрити",
  "pricing.checkout.modal.aria_label": "Оформлення оплати",
  "pricing.checkout.email.label": "Ваш email",
  "pricing.checkout.email.placeholder": "you@clinic.com",
  "pricing.checkout.email.invalid": "Введіть коректну email-адресу",
  "pricing.checkout.tier.prefix": "Тариф ",
  "pricing.checkout.submit.label": "Перейти до оплати",
  "pricing.checkout.submit.loading": "Зачекайте…",
  "pricing.checkout.magic.sent_title": "Перевірте пошту",
  "pricing.checkout.magic.sent_body": "Ми надіслали лист для підтвердження.",
  "pricing.checkout.magic.resend": "Надіслати ще раз",
  "pricing.checkout.magic.resend_disabled_hint": "Доступно за {{seconds}} сек",
  "pricing.checkout.error.turnstile": "Перевірка безпеки не пройшла.",
  "pricing.checkout.error.busy": "Запит уже обробляється.",
  "pricing.checkout.error.ratelimited": "Забагато спроб. Спробуйте пізніше.",
  "pricing.checkout.error.network": "Проблема зі з'єднанням.",
  "pricing.checkout.error.generic": "Щось пішло не так.",
};

function tStr(key, params) {
  const raw =
    window.i18n && typeof window.i18n.t === "function"
      ? window.i18n.t(key, FALLBACK_STRINGS[key])
      : FALLBACK_STRINGS[key] || key;
  if (!params) return raw;
  return String(raw).replace(/\{\{(\w+)\}\}/g, (_, name) =>
    params[name] === undefined ? "" : String(params[name])
  );
}

// ---------------- Error type → i18n key ----------------
function errorKeyFromProblem(problem) {
  const type = (problem && problem.type) || "";
  if (type === "urn:hb:pay:turnstile-failed") return "pricing.checkout.error.turnstile";
  if (type === "urn:hb:pay:idempotency-inflight") return "pricing.checkout.error.busy";
  if (type === "urn:hb:pay:rate-limited" || type === "urn:hb:pay:magic-link-rate-limited")
    return "pricing.checkout.error.ratelimited";
  return "pricing.checkout.error.generic";
}

// ---------------- Imperative submit to WayForPay ----------------
function submitToWayForPay(redirectUrl, formFields) {
  const form = document.createElement("form");
  form.method = "POST";
  form.action = redirectUrl;
  form.style.display = "none";
  Object.keys(formFields).forEach((key) => {
    const input = document.createElement("input");
    input.type = "hidden";
    input.name = key;
    input.value = String(formFields[key]);
    form.appendChild(input);
  });
  document.body.appendChild(form);
  form.submit();
}

// ---------------- Strip ?mlt= from the address bar ----------------
// Magic-link tokens are single-use. After consumption, remove them from the
// URL so they don't leak via browser history / Referer / telemetry.
function stripMagicLinkFromUrl() {
  try {
    const url = new URL(window.location.href);
    let changed = false;
    if (url.searchParams.has("mlt")) {
      url.searchParams.delete("mlt");
      changed = true;
    }
    if (url.searchParams.has("tier")) {
      // `tier` is only there for the resume flow — no reason to keep it.
      url.searchParams.delete("tier");
      changed = true;
    }
    if (url.hash === "#checkout-resume") {
      url.hash = "";
      changed = true;
    }
    if (changed && typeof history !== "undefined" && typeof history.replaceState === "function") {
      history.replaceState(null, "", url.pathname + (url.search || "") + (url.hash || ""));
    }
  } catch (_err) {
    /* noop — old browsers that lack URL() fall through */
  }
}

// ---------------- Turnstile widget helpers ----------------
// Returns a fresh challenge token. Renders the widget on first call and keeps
// the rendered instance around to reset() between submissions. On dev builds
// where Turnstile isn't loaded (no site key inlined yet), returns the literal
// string "dev-bypass-token" — the server rejects this value in production but
// accepts it when TURNSTILE_SECRET starts with DEV_BYPASS_.
function ensureTurnstileToken(containerId) {
  return new Promise((resolve) => {
    const siteKey = window.__TURNSTILE_SITE_KEY__;
    const api = window.turnstile;
    if (!siteKey || !api || typeof api.render !== "function") {
      // Dev fallback — production startup rejects DEV_BYPASS outside NODE_ENV=development.
      resolve("dev-bypass-token");
      return;
    }
    const el = document.getElementById(containerId);
    if (!el) {
      resolve("dev-bypass-token");
      return;
    }
    // If a widget was previously rendered here, reset it (gets a new challenge).
    if (el.dataset.tsid) {
      try {
        api.reset(el.dataset.tsid);
      } catch (_e) {
        /* noop */
      }
    }
    let resolved = false;
    const id = api.render(el, {
      "sitekey": siteKey,
      "size": "invisible",
      "callback": (token) => {
        if (resolved) return;
        resolved = true;
        resolve(token || "");
      },
      "error-callback": () => {
        if (resolved) return;
        resolved = true;
        resolve("");
      },
      "timeout-callback": () => {
        if (resolved) return;
        resolved = true;
        resolve("");
      },
    });
    el.dataset.tsid = String(id);
    // Invisible widget runs automatically; no explicit execute() needed in
    // Cloudflare Turnstile (unlike hCaptcha). If render didn't trigger the
    // challenge (e.g., network hiccup), fall back after a short delay.
    setTimeout(() => {
      if (!resolved) {
        try {
          api.execute(id);
        } catch (_e) {
          /* noop */
        }
      }
    }, 150);
    setTimeout(() => {
      if (!resolved) {
        resolved = true;
        resolve("");
      }
    }, 15000);
  });
}

// ---------------- Main component ----------------
function CheckoutModal() {
  const [open, setOpen] = useStateCM(false);
  const [tier, setTier] = useStateCM("pro");
  const [email, setEmail] = useStateCM("");
  const [submitting, setSubmitting] = useStateCM(false);
  const [error, setError] = useStateCM(""); // translated error string
  const [magicSent, setMagicSent] = useStateCM(false);
  const [sentEmail, setSentEmail] = useStateCM("");
  const [resendIn, setResendIn] = useStateCM(0);

  const dialogRef = useRefCM(null);
  const previousFocusRef = useRefCM(null);
  const emailInputRef = useRefCM(null);

  // -------------- Opening / closing --------------
  const openModal = useCallbackCM((opts) => {
    previousFocusRef.current = document.activeElement;
    setTier((opts && opts.tier) || "pro");
    setEmail("");
    setError("");
    setMagicSent(false);
    setSentEmail("");
    setResendIn(0);
    setOpen(true);
    // Focus the email input after paint
    setTimeout(() => {
      if (emailInputRef.current) emailInputRef.current.focus();
    }, 0);
  }, []);

  const closeModal = useCallbackCM(() => {
    setOpen(false);
    setTimeout(() => {
      if (previousFocusRef.current && typeof previousFocusRef.current.focus === "function") {
        previousFocusRef.current.focus();
      }
    }, 0);
  }, []);

  // Expose imperative API
  useEffectCM(() => {
    window.CheckoutModal = window.CheckoutModal || {};
    window.CheckoutModal.open = openModal;
    window.CheckoutModal.close = closeModal;
  }, [openModal, closeModal]);

  // -------------- Escape key + focus trap --------------
  useEffectCM(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        closeModal();
      } else if (e.key === "Tab" && dialogRef.current) {
        const focusable = dialogRef.current.querySelectorAll(
          'button, input, select, textarea, a[href], [tabindex]:not([tabindex="-1"])'
        );
        if (!focusable.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, closeModal]);

  // -------------- Resend countdown --------------
  useEffectCM(() => {
    if (resendIn <= 0) return undefined;
    const t = setTimeout(() => setResendIn(resendIn - 1), 1000);
    return () => clearTimeout(t);
  }, [resendIn]);

  // -------------- Detect magic-link return --------------
  useEffectCM(() => {
    const params = new URLSearchParams(window.location.search);
    const mlt = params.get("mlt");
    const resumeTier = params.get("tier");
    if (!mlt || !resumeTier) return undefined;
    // Auto-open + auto-submit with the stashed email
    const savedEmail = sessionStorage.getItem("hb_checkout_email");
    const savedIdem = sessionStorage.getItem("hb_checkout_idem");
    if (!savedEmail || !savedIdem) return undefined;
    openModal({ tier: resumeTier });
    setEmail(savedEmail);
    // Submit on next tick so state has flushed
    setTimeout(() => {
      submitCheckout({
        tier: resumeTier,
        email: savedEmail,
        idemKey: savedIdem,
        magicLinkToken: mlt,
      });
    }, 100);
  }, []); // run once

  // -------------- Submit handler --------------
  const submitCheckout = async ({
    tier: submitTier,
    email: submitEmail,
    idemKey,
    magicLinkToken,
  }) => {
    setSubmitting(true);
    setError("");

    // Fetch a fresh Turnstile challenge token. Production rejects the
    // "dev-bypass-token" fallback — see env.ts / turnstile.ts.
    const turnstileToken = await ensureTurnstileToken("hb-turnstile-container");

    const locale = (window.i18n && window.i18n.getLocale && window.i18n.getLocale()) || "uk";

    try {
      const res = await fetch("/api/pay/create-order", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Idempotency-Key": idemKey,
        },
        body: JSON.stringify({
          email: submitEmail,
          tier: submitTier,
          locale,
          turnstileToken,
          magicLinkToken: magicLinkToken || undefined,
        }),
      });

      const body = await res.json();

      if (res.status === 200 && body.status === "ready") {
        // Clear session storage; strip the magic-link token from the URL
        // (so it doesn't leak via history/Referer) BEFORE redirecting.
        sessionStorage.removeItem("hb_checkout_email");
        sessionStorage.removeItem("hb_checkout_idem");
        stripMagicLinkFromUrl();
        submitToWayForPay(body.redirectUrl, body.formFields);
        return;
      }

      if (res.status === 202 && body.status === "magic_link_sent") {
        setMagicSent(true);
        setSentEmail(body.email || submitEmail);
        setResendIn(body.resendAfterSeconds || 60);
        setSubmitting(false);
        return;
      }

      // Error path — regardless of outcome, drop the single-use ?mlt= token
      // from the URL so a refresh doesn't re-consume it.
      if (magicLinkToken) stripMagicLinkFromUrl();
      setError(tStr(errorKeyFromProblem(body)));
      setSubmitting(false);
    } catch (_err) {
      if (magicLinkToken) stripMagicLinkFromUrl();
      setError(tStr("pricing.checkout.error.network"));
      setSubmitting(false);
    }
  };

  const onSubmit = (e) => {
    e.preventDefault();
    if (submitting) return;
    const emailTrim = (email || "").trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailTrim)) {
      setError(tStr("pricing.checkout.email.invalid"));
      return;
    }
    const idemKey =
      (crypto && crypto.randomUUID && crypto.randomUUID()) ||
      "hb-" + Date.now() + "-" + Math.random().toString(36).slice(2, 10);
    sessionStorage.setItem("hb_checkout_email", emailTrim);
    sessionStorage.setItem("hb_checkout_idem", idemKey);
    submitCheckout({ tier, email: emailTrim, idemKey });
  };

  if (!open) return null;

  const titleId = "hb-checkout-title";
  const tierLabel =
    (window.i18n && window.i18n.t
      ? window.i18n.t("pricing.tier." + tier + ".title")
      : tier.toUpperCase()) || tier.toUpperCase();

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      aria-label={tStr("pricing.checkout.modal.aria_label")}
      onClick={(e) => {
        if (e.target === e.currentTarget) closeModal();
      }}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        background: "rgba(26, 24, 21, 0.55)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
      }}
    >
      <div
        ref={dialogRef}
        style={{
          background: "#ffffff",
          borderRadius: 16,
          maxWidth: 420,
          width: "100%",
          padding: 28,
          boxShadow: "0 10px 40px rgba(0,0,0,0.2)",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 16,
          }}
        >
          <h3 id={titleId} style={{ margin: 0, fontSize: 20 }}>
            {tStr("pricing.checkout.modal.title")}
          </h3>
          <button
            type="button"
            onClick={closeModal}
            aria-label={tStr("pricing.checkout.modal.close")}
            style={{
              background: "transparent",
              border: "none",
              fontSize: 24,
              cursor: "pointer",
              color: "#5a554e",
              lineHeight: 1,
            }}
          >
            ×
          </button>
        </div>

        <div style={{ fontSize: 13, color: "#5a554e", marginBottom: 16 }}>
          {tStr("pricing.checkout.tier.prefix")}
          <strong style={{ color: "#1a1815" }}>{tierLabel}</strong>
        </div>

        {!magicSent ? (
          <form onSubmit={onSubmit}>
            <label
              htmlFor="hb-checkout-email"
              style={{ display: "block", fontSize: 13, marginBottom: 6, color: "#5a554e" }}
            >
              {tStr("pricing.checkout.email.label")}
            </label>
            <input
              ref={emailInputRef}
              id="hb-checkout-email"
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={tStr("pricing.checkout.email.placeholder")}
              style={{
                width: "100%",
                padding: "10px 12px",
                border: "1px solid #e8e2d4",
                borderRadius: 8,
                fontSize: 15,
                marginBottom: 12,
                boxSizing: "border-box",
              }}
            />
            {error && (
              <div role="alert" style={{ color: "#d0482f", fontSize: 13, marginBottom: 12 }}>
                {error}
              </div>
            )}
            <button
              type="submit"
              disabled={submitting}
              className="btn btn-primary"
              style={{ width: "100%", padding: "12px 16px", fontSize: 15 }}
            >
              {submitting
                ? tStr("pricing.checkout.submit.loading")
                : tStr("pricing.checkout.submit.label")}
            </button>
            {/* Cloudflare Turnstile invisible widget — rendered on demand by ensureTurnstileToken(). */}
            <div id="hb-turnstile-container" style={{ marginTop: 8 }} aria-hidden="true"></div>
          </form>
        ) : (
          <div>
            <h4 style={{ margin: "0 0 12px", fontSize: 17 }}>
              {tStr("pricing.checkout.magic.sent_title")}
            </h4>
            <p style={{ color: "#5a554e", fontSize: 14, marginBottom: 16 }}>
              {tStr("pricing.checkout.magic.sent_body", { email: sentEmail })}
            </p>
            <button
              type="button"
              disabled={resendIn > 0}
              onClick={() => {
                const idemKey =
                  (crypto && crypto.randomUUID && crypto.randomUUID()) ||
                  "hb-" + Date.now() + "-" + Math.random().toString(36).slice(2, 10);
                sessionStorage.setItem("hb_checkout_idem", idemKey);
                submitCheckout({ tier, email: sentEmail, idemKey });
              }}
              className="btn btn-ghost"
              style={{ width: "100%", padding: "12px 16px", fontSize: 14 }}
            >
              {resendIn > 0
                ? tStr("pricing.checkout.magic.resend_disabled_hint", { seconds: resendIn })
                : tStr("pricing.checkout.magic.resend")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// Turnstile calls this via the `?onload=` query string in index.html — it is
// intentionally a no-op. Widgets are rendered lazily on modal open via
// ensureTurnstileToken(). Without this stub, the Cloudflare script logs a
// warning to the console until React mounts.
if (typeof window.__hb_turnstileOnLoad !== "function") {
  window.__hb_turnstileOnLoad = function () {
    /* noop */
  };
}

window.CheckoutModal = CheckoutModal;
